import { isPlayedResult, type Colour, type RoundEntry } from '../model/types.js';

/** Strength of a colour preference (FIDE Dutch A.6). */
export type PreferenceStrength = 'absolute' | 'strong' | 'mild' | 'none';

export interface ColourState {
  /** Colours of played games, in round order. */
  readonly colours: readonly Colour[];
  /** whites - blacks over played games. */
  readonly difference: number;
  readonly preference: Colour | null;
  readonly strength: PreferenceStrength;
}

/**
 * Colours of the games a player actually played. Forfeits and byes carry no
 * colour for pairing purposes (FIDE C.04.1, unplayed games).
 */
export function playedColours(history: readonly RoundEntry[]): Colour[] {
  const out: Colour[] = [];
  for (const r of history) {
    if (r.colour !== null && isPlayedResult(r.result)) out.push(r.colour);
  }
  return out;
}

/** Colour difference and preference per the FIDE Dutch rules (A.6). */
export function colourState(history: readonly RoundEntry[]): ColourState {
  const colours = playedColours(history);
  let difference = 0;
  for (const c of colours) difference += c === 'w' ? 1 : -1;
  const last = colours[colours.length - 1];
  const prev = colours[colours.length - 2];
  if (last === undefined) {
    return { colours, difference, preference: null, strength: 'none' };
  }
  const twoSame = prev !== undefined && prev === last;
  if (difference < -1 || (twoSame && last === 'b')) {
    return { colours, difference, preference: 'w', strength: 'absolute' };
  }
  if (difference > 1 || (twoSame && last === 'w')) {
    return { colours, difference, preference: 'b', strength: 'absolute' };
  }
  if (difference === -1) return { colours, difference, preference: 'w', strength: 'strong' };
  if (difference === 1) return { colours, difference, preference: 'b', strength: 'strong' };
  // difference 0: mild preference to alternate from the last game
  return { colours, difference, preference: last === 'w' ? 'b' : 'w', strength: 'mild' };
}

const RANK: Record<PreferenceStrength, number> = { absolute: 3, strong: 2, mild: 1, none: 0 };

export type ColourRule = '5.2.1' | '5.2.2' | '5.2.3' | '5.2.4' | '5.2.5';

export const COLOUR_RULE_TEXT: Record<ColourRule, string> = {
  '5.2.1': 'both colour preferences granted',
  '5.2.2': 'the stronger colour preference granted',
  '5.2.3': 'colours alternated from the last round they differed',
  '5.2.4': "the higher-ranked player's preference granted",
  '5.2.5': 'no preferences: initial colour by pairing-number parity',
};

/**
 * Allocate colours between two players (FIDE Dutch Article 5.2) and say which
 * rule decided. `higher` is the higher-ranked player (score, then pairing
 * number); `higherPairingNumber` drives 5.2.5 when neither has a preference.
 */
export function allocateColoursWithRule(
  higher: ColourState,
  lower: ColourState,
  higherPairingNumber: number,
  initialColour: Colour,
): { colour: Colour; rule: ColourRule } {
  const hp = higher.preference;
  const lp = lower.preference;
  const flip = (c: Colour): Colour => (c === 'w' ? 'b' : 'w');
  if (hp !== null && lp !== null && hp !== lp) return { colour: hp, rule: '5.2.1' };
  if (hp !== null && lp === null) return { colour: hp, rule: '5.2.1' };
  if (hp === null && lp !== null) return { colour: flip(lp), rule: '5.2.1' };
  if (hp !== null && lp !== null) {
    const hs = RANK[higher.strength];
    const ls = RANK[lower.strength];
    if (hs !== ls) return { colour: hs > ls ? hp : flip(hp), rule: '5.2.2' };
    if (higher.strength === 'absolute') {
      const hd = Math.abs(higher.difference);
      const ld = Math.abs(lower.difference);
      if (hd !== ld) return { colour: hd > ld ? hp : flip(hp), rule: '5.2.2' };
    }
    const hc = higher.colours;
    const lc = lower.colours;
    for (let i = 1; i <= Math.min(hc.length, lc.length); i++) {
      const a = hc[hc.length - i] as Colour;
      const b = lc[lc.length - i] as Colour;
      if (a !== b) return { colour: flip(a), rule: '5.2.3' };
    }
    return { colour: hp, rule: '5.2.4' };
  }
  const odd = higherPairingNumber % 2 === 1;
  return { colour: odd ? initialColour : flip(initialColour), rule: '5.2.5' };
}

/** Allocate colours (Article 5.2); returns the higher-ranked player's colour. */
export function allocateColours(
  higher: ColourState,
  lower: ColourState,
  higherPairingNumber: number,
  initialColour: Colour,
): Colour {
  return allocateColoursWithRule(higher, lower, higherPairingNumber, initialColour).colour;
}
